import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const AdvertisementPreviewModal = ({ product, isOpen, onClose }) => {
  const [activeMarketplace, setActiveMarketplace] = useState('ML');

  // Calculate marketplace listing prices based on user's desired selling price
  const calculations = useMemo(() => {
    const cost = parseFloat(product?.cost) || 0;
    const userPrice = parseFloat(product?.price) || 0;
    
    if (cost === 0 || userPrice === 0) return null;

    // Mercado Livre Classic (11.5% + R$6.25)
    const mlClassicFeePercent = 11.5;
    const mlClassicFixedCost = 6.25;
    const mlClassicListingPrice = (userPrice + mlClassicFixedCost) / (1 - mlClassicFeePercent / 100);
    const mlClassicFee = (mlClassicListingPrice * mlClassicFeePercent) / 100;
    const mlClassicProfit = userPrice - cost;
    const mlClassicMargin = userPrice > 0 ? (mlClassicProfit / userPrice) * 100 : 0;

    // Mercado Livre Premium (16.5% + R$6.25)
    const mlPremiumFeePercent = 16.5;
    const mlPremiumFixedCost = 6.25;
    const mlPremiumListingPrice = (userPrice + mlPremiumFixedCost) / (1 - mlPremiumFeePercent / 100);
    const mlPremiumFee = (mlPremiumListingPrice * mlPremiumFeePercent) / 100;
    const mlPremiumProfit = userPrice - cost;
    const mlPremiumMargin = userPrice > 0 ? (mlPremiumProfit / userPrice) * 100 : 0;

    // Shopee (14% + R$3)
    const shopeeFeePercent = 14;
    const shopeeFixedFee = 3;
    const shopeeListingPrice = (userPrice + shopeeFixedFee) / (1 - shopeeFeePercent / 100);
    const shopeeTotalFees = (shopeeListingPrice * shopeeFeePercent) / 100 + shopeeFixedFee;
    const shopeeProfit = userPrice - cost;
    const shopeeMargin = userPrice > 0 ? (shopeeProfit / userPrice) * 100 : 0;

    // Amazon (15% + R$5)
    const amazonFeePercent = 15;
    const amazonFixedFee = 5;
    const amazonListingPrice = (userPrice + amazonFixedFee) / (1 - amazonFeePercent / 100);
    const amazonTotalFees = (amazonListingPrice * amazonFeePercent) / 100 + amazonFixedFee;
    const amazonProfit = userPrice - cost;
    const amazonMargin = userPrice > 0 ? (amazonProfit / userPrice) * 100 : 0;

    return {
      mercadolivreClassic: {
        listingPrice: mlClassicListingPrice,
        profit: mlClassicProfit,
        margin: mlClassicMargin,
        fee: mlClassicFee,
        fixedCost: mlClassicFixedCost
      },
      mercadolivrePremium: {
        listingPrice: mlPremiumListingPrice,
        profit: mlPremiumProfit,
        margin: mlPremiumMargin,
        fee: mlPremiumFee,
        fixedCost: mlPremiumFixedCost
      },
      shopee: {
        listingPrice: shopeeListingPrice,
        profit: shopeeProfit,
        margin: shopeeMargin,
        totalFees: shopeeTotalFees
      },
      amazon: {
        listingPrice: amazonListingPrice,
        profit: amazonProfit,
        margin: amazonMargin,
        totalFees: amazonTotalFees
      }
    };
  }, [product?.cost, product?.price]);

  // Calculate field completion
  const analysis = useMemo(() => {
    const cost = parseFloat(product?.cost) || 0;
    const price = parseFloat(product?.price) || 0;
    const profitMargin = cost > 0 && price > 0 ? ((price - cost) / price) * 100 : 0;

    // Calculate cubic weight
    const width = parseFloat(product?.width) || 0;
    const height = parseFloat(product?.height) || 0;
    const length = parseFloat(product?.length) || 0;
    const cubicWeight = (width * height * length) / 6000;

    // Calculate field completion percentage
    const requiredFields = [
      product?.name,
      product?.sku,
      product?.description,
      product?.price,
      product?.cost,
      product?.stock,
      product?.category,
      product?.brand,
      product?.images?.[0]
    ];
    const filledFields = requiredFields?.filter(field => field)?.length;
    const completionPercentage = (filledFields / requiredFields?.length) * 100;

    return {
      profitMargin: profitMargin?.toFixed(1),
      cubicWeight: cubicWeight?.toFixed(2),
      completionPercentage: Math.round(completionPercentage),
      isComplete: completionPercentage === 100
    };
  }, [product]);

  const marketplaces = [
    { 
      id: 'ML', 
      name: 'Mercado Livre', 
      color: 'bg-yellow-400 text-gray-900',
      logo: '🛒',
      bgColor: 'bg-yellow-50'
    },
    { 
      id: 'Shopee', 
      name: 'Shopee', 
      color: 'bg-orange-500 text-white',
      logo: '🛍️',
      bgColor: 'bg-orange-50'
    },
    { 
      id: 'Amazon', 
      name: 'Amazon', 
      color: 'bg-gray-900 text-white',
      logo: '📦',
      bgColor: 'bg-gray-50'
    }
  ];

  // Get current marketplace price
  const getCurrentPrice = () => {
    if (!calculations) return parseFloat(product?.price) || 0;
    
    switch(activeMarketplace) {
      case 'ML':
        return calculations?.mercadolivreClassic?.listingPrice || 0;
      case 'Shopee':
        return calculations?.shopee?.listingPrice || 0;
      case 'Amazon':
        return calculations?.amazon?.listingPrice || 0;
      default:
        return parseFloat(product?.price) || 0;
    }
  };

  // Get current marketplace profit margin
  const getCurrentMargin = () => {
    if (!calculations) return parseFloat(analysis?.profitMargin) || 0;
    
    switch(activeMarketplace) {
      case 'ML':
        return calculations?.mercadolivreClassic?.margin || 0;
      case 'Shopee':
        return calculations?.shopee?.margin || 0;
      case 'Amazon':
        return calculations?.amazon?.margin || 0;
      default:
        return parseFloat(analysis?.profitMargin) || 0;
    }
  };

  if (!isOpen) return null;

  const currentPrice = getCurrentPrice();
  const currentMargin = getCurrentMargin();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">Preview do Anúncio</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 hover:bg-gray-100 rounded-lg flex items-center justify-center transition-smooth"
          >
            <Icon name="X" size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Marketplace Tabs */}
          <div className="flex gap-2">
            {marketplaces?.map((marketplace) => (
              <button
                key={marketplace?.id}
                onClick={() => setActiveMarketplace(marketplace?.id)}
                className={`
                  flex-1 px-3 py-2.5 rounded-lg font-semibold text-sm transition-smooth flex items-center justify-center gap-2
                  ${
                    activeMarketplace === marketplace?.id
                      ? marketplace?.color + ' shadow-md'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }
                `}
              >
                <span className="text-lg">{marketplace?.logo}</span>
                <span>{marketplace?.name}</span>
              </button>
            ))}
          </div>

          {/* Product Image */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center">
            {product?.images?.[0] ? (
              <img
                src={product?.images?.[0]}
                alt={product?.name}
                className="max-w-full max-h-64 object-contain"
              />
            ) : (
              <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon name="ImageOff" size={48} className="text-gray-400" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {product?.name || 'Nome do Produto'}
            </h3>
            
            {/* SKU Display - Show all SKUs for kits */}
            {product?.productType === 'kit' && product?.kitComponents?.length > 0 ? (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-semibold text-blue-900 mb-2">SKUs do Kit:</p>
                <div className="space-y-1">
                  {product?.kitComponents?.map((component, index) => (
                    <div key={index} className="text-xs text-blue-700">
                      • {component?.sku} - {component?.name}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 mb-3">SKU: {product?.sku}</p>
            )}
            
            <p className="text-3xl font-bold text-blue-600 mb-3">
              R$ {currentPrice?.toFixed(2)}
            </p>
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Icon name="Clock" size={14} />
                <span>Condição: {product?.condition || 'Novo'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Truck" size={14} />
                <span>Frete Grátis</span>
              </div>
            </div>
          </div>

          {/* Price Display */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5">
            <p className="text-sm text-gray-600 mb-1">Preço de Listagem</p>
            <p className="text-4xl font-bold text-gray-900 mb-3">
              R$ {currentPrice?.toFixed(2)}
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white/80 rounded-lg p-3">
                <p className="text-gray-600 mb-1">Você Recebe</p>
                <p className="text-xl font-bold text-green-600">R$ {parseFloat(product?.price || 0)?.toFixed(2)}</p>
              </div>
              <div className="bg-white/80 rounded-lg p-3">
                <p className="text-gray-600 mb-1">Margem</p>
                <p className={`text-xl font-bold ${
                  currentMargin < 0 ? 'text-red-600' :
                  currentMargin < 15 ? 'text-orange-600' :
                  currentMargin <= 35 ? 'text-green-600' : 'text-blue-600'
                }`}>
                  {currentMargin?.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          {/* Analysis Cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Package" size={16} className="text-blue-600" />
                <p className="text-xs font-semibold text-blue-900">PESO CÚBICO</p>
              </div>
              <p className="text-2xl font-bold text-blue-600">{analysis?.cubicWeight} kg</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle" size={16} className="text-purple-600" />
                <p className="text-xs font-semibold text-purple-900">COMPLETUDE</p>
              </div>
              <p className="text-2xl font-bold text-purple-600">{analysis?.completionPercentage}%</p>
            </div>
          </div>

          {/* Completion Progress */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-900">Campos Preenchidos</p>
              <p className="text-sm font-bold text-gray-900">{analysis?.completionPercentage}%</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${
                  analysis?.isComplete
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500' :'bg-gradient-to-r from-blue-500 to-purple-500'
                }`}
                style={{ width: `${analysis?.completionPercentage}%` }}
              ></div>
            </div>
            {analysis?.isComplete && (
              <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                <Icon name="CheckCircle" size={12} />
                Produto pronto para publicação!
              </p>
            )}
          </div>

          {/* Kit Components Display */}
          {product?.productType === 'kit' && product?.metadata?.kitComponents?.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Package" size={16} className="text-blue-600" />
                <h4 className="text-sm font-bold text-blue-900">PRODUTOS DO KIT</h4>
              </div>
              <div className="space-y-2">
                {product?.metadata?.kitComponents?.map((component, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{component?.name}</p>
                      <p className="text-xs text-gray-500">SKU: {component?.sku}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">x{component?.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .transition-smooth {
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default AdvertisementPreviewModal;